import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import {Box, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Skeleton, Typography} from "@mui/material";
import {Add, DeleteOutlined, EditOutlined} from "@mui/icons-material";
import Button from "../../../../../components/base/button/button";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import SearchInput from "../../../../../components/base/input/search_input";
import Grid from "@mui/material/Unstable_Grid2";
import {Checkbox} from "../../../../../components/base/checkbox/checkbox";
import classnames from "classnames";
import Form, {MuiRFF, Yup} from '../../../../../components/form';
import {ReactComponent as SaveIcon} from "../../../../../../assets/images/save-rollback/check.svg";
import {ReactComponent as CancelIcon} from "../../../../../../assets/images/save-rollback/cancel.svg";

export const ManageTechnicalPropertyTemplatesDialog = ({
                                                           open,
                                                           setOpen,
                                                           industryId,
                                                           getVisibleProperties,
                                                           loadingProperties,
                                                           typeNames,
                                                       }) => {
    const [removingIds, setRemovingIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const isMounted = useIsMounted();

    const selectedTemplate = useMemo(() => templates.find(e => e.id === selectedTemplateId), [selectedTemplateId, templates]);

    /**
     * Fetch templates for technical properties
     * @type {(function(): void)}
     */
    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        const response = await Api.listIndustryPropertyTemplate(industryId);
        if (!isMounted())
            return;
        setLoading(false);
        if (response.resultFlag) {
            setTemplates(response.data);
        }
    }, [isMounted, industryId])

    /**
     * Fetch templates on the component mount
     */
    useEffect(() => {
        if (open && !!industryId) {
            void fetchTemplates();
        }
    }, [fetchTemplates, open, industryId])

    /**
     * Invokes the [onSelect] callback with the selected template.
     */
    const onTemplateSelected = useCallback(async (template) => {
        if (!template)
            return;
        if (template.fetchedProperties) {
            setSelectedTemplateId(template.id);
            return;
        }
        const response = await Api.getIndustryPropertyTemplate(template.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const selectedPropertyIds = response.data?.industryProperties.map(e => e.id) ?? []
            setTemplates(prevState => prevState.map(e => e.id === template.id ? {...e, fetchedProperties: true, selectedPropertyIds: selectedPropertyIds} : e));
            setSelectedTemplateId(template.id);
        }
    }, [isMounted]);

    /**
     * Remove a template
     * @type {(function(*): Promise<void>)}
     */
    const removeTemplate = useCallback(async (templateId) => {
        setRemovingIds(prevState => [...prevState, templateId]);
        const response = await Api.deleteIndustryPropertyTemplate(templateId);
        if (!isMounted())
            return;
        setRemovingIds(prevState => prevState.filter(e => e !== templateId));
        if (response.resultFlag) {
            setTemplates(prevState => prevState.filter(e => e.id !== templateId));
            setSelectedTemplateId(prevState => prevState === templateId ? null : prevState);
        }
    }, [isMounted])

    /**
     * Handles when a template is upserted.
     * @type {(function(*): void)}
     */
    const onTemplateUpserted = useCallback((template) => {
        setTemplates(prevState => {
            const found = prevState.find(e => e.id === template.id);
            if (!found)
                return [...prevState, template];
            return prevState.map(e => e.id === template.id ? template : e);
        });
    }, [])

    return (
        <>
            <Dialog
                maxWidth={"sm"}
                fullWidth
                onClose={() => setOpen(false)}
                open={open}
            >
                <DialogTitle fontWeight={600}>
                    Manage Templates
                </DialogTitle>
                <DialogContent>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        {
                            loading &&
                            Array.from({length: 4}).map((_, i) => (
                                <Skeleton key={`loading-${i}`} variant="rectangular" sx={{minHeight: 65, width: '100%', transform: 'none'}}/>
                            ))
                        }
                        {
                            !loading && !templates.length &&
                            <Typography variant={'body2'} textAlign={'center'} sx={{marginBlock: 3}}>
                                No templates found. please create one using the button below.
                            </Typography>
                        }
                        {
                            !loading && !!templates.length &&
                            templates.map((template) => (
                                <Card
                                    key={template.id}
                                    value={template.id}
                                    variant={'outlined'}
                                    sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, padding: 2}}
                                >
                                    <Typography variant={'body1'} fontWeight={500}>
                                        {template.title}
                                    </Typography>
                                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end'}}>
                                        <IconButton
                                            onClick={() => onTemplateSelected(template)}
                                            color={'primary'}
                                            size={'small'}
                                            sx={{backgroundColor: 'background.paper'}}
                                        >
                                            <EditOutlined/>
                                        </IconButton>
                                        <IconButton
                                            onClick={() => removeTemplate(template.id)}
                                            disabled={removingIds.includes(template.id)}
                                            color={'error'}
                                            size={'small'}
                                            sx={{backgroundColor: 'background.paper'}}
                                        >
                                            {
                                                removingIds.includes(template.id)
                                                    ? <CircularProgress size={15} color={'primary'}/>
                                                    : <DeleteOutlined/>
                                            }
                                        </IconButton>
                                    </Box>
                                </Card>
                            ))
                        }
                    </Box>
                    <UpsertTechnicalPropertyTemplateDialog
                        open={!!selectedTemplateId}
                        creating={selectedTemplateId === 'new'}
                        setOpen={() => setSelectedTemplateId(null)}
                        industryId={industryId}
                        getVisibleProperties={getVisibleProperties}
                        selectedTemplate={selectedTemplate}
                        loadingProperties={loadingProperties}
                        typeNames={typeNames}
                        onTemplateUpserted={onTemplateUpserted}
                    />
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', p: 0}}>
                    <Button
                        variant={'dialog'}
                        type={'button'}
                        color={'primary'}
                        borderRadius={'0 8px 0 8px'}
                        onClick={() => setOpen(false)}
                    >
                        Close
                    </Button>
                    {
                        !loading &&
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => setSelectedTemplateId('new')}
                            borderRadius={'8px 0 8px 0'}
                        >
                            <Add className={'mr-2'}/>
                            <p>
                                Create New Template
                            </p>
                        </Button>
                    }
                </DialogActions>
            </Dialog>
        </>
    );
}


const UpsertTechnicalPropertyTemplateDialog = ({
                                                   open,
                                                   setOpen,
                                                   industryId,
                                                   getVisibleProperties,
                                                   selectedTemplate,
                                                   onTemplateUpserted,
                                                   creating,
                                                   loadingProperties,
                                                   typeNames,
                                               }) => {
    const [keywords, setKeywords] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const [_standardProperties, _customProperties] = useMemo(() => {
        if (!open)
            return [[], []];
        const standardProperties = [];
        const customProperties = [];
        for (const property of getVisibleProperties(keywords)) {
            if (property.isDefault) {
                standardProperties.push(property);
            } else {
                customProperties.push(property);
            }
        }
        return [standardProperties, customProperties]
    }, [getVisibleProperties, keywords, open])

    const schema = useMemo(() => Yup.object({
        title: Yup.string().required(ValidateMessages.required).trim(),
        selectedPropertyIds: Yup.array().of(Yup.number()).required(ValidateMessages.required),
    }), []);

    const initialValues = useMemo(() => ({
        title: selectedTemplate?.title ?? "",
        selectedPropertyIds: selectedTemplate?.selectedPropertyIds ?? [],
    }), [selectedTemplate?.selectedPropertyIds, selectedTemplate?.title])

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Replaces the template with the current query.
     */
    const setTemplateProperties = useCallback(async (template) => {
        const forApi = {
            propertiesId: template.selectedPropertyIds,
            templateId: template.id,
        }
        const response = await Api.setPropertiesForIndustryPropertyTemplate(forApi);
        if (!isMounted())
            return false;
        return response?.resultFlag ?? false;
    }, [isMounted]);

    /**
     * Upserts the template.
     * @type {function(*): Promise<(*&{title: *, selectedPropertyIds: []|*})|{id: *, title: *, selectedPropertyIds: []|*}>}
     */
    const upsertTemplate = useCallback(async (values) => {
        let template;
        if (creating) {
            const response = await Api.createIndustryPropertyTemplate({
                industryId: industryId,
                title: values.title,
            })
            if (response?.resultFlag && !response?.aborted) {
                template = {id: response.data.id, title: values.title, selectedPropertyIds: values.selectedPropertyIds,}
            }
        } else {
            const response = await Api.updateIndustryPropertyTemplate({
                id: selectedTemplate.id,
                title: values.title,
            });
            if (response?.resultFlag && !response?.aborted) {
                template = {...selectedTemplate, title: values.title, selectedPropertyIds: values.selectedPropertyIds}
            }
        }
        return template;
    }, [creating, industryId, selectedTemplate])


    /**
     * Submits the form values to save a new template.
     */
    const submitTemplate = useCallback(async (values) => {
        setSubmitting(true);
        const template = await upsertTemplate(values);
        if (!isMounted() || !template)
            return;
        const resultFlag = await setTemplateProperties(template);
        if (!isMounted())
            return;
        if (resultFlag) {
            onTemplateUpserted({
                ...template,
                selectedPropertyIds: values.selectedPropertyIds
            });
            setOpen(false);
        }
        setSubmitting(false);
    }, [isMounted, onTemplateUpserted, setOpen, setTemplateProperties, upsertTemplate]);

    return (
        <>
            <Dialog
                maxWidth={"md"}
                fullWidth
                onClose={() => setOpen(false)}
                open={open}
            >
                <DialogTitle fontWeight={600}>
                    {creating ? "Create New" : "Update"} Template
                </DialogTitle>
                <DialogContent>
                    <Form
                        onSubmit={submitTemplate}
                        validate={validate}
                        initialValues={initialValues}
                        formProps={{id: 'upsert-template-form'}}
                    >
                        {({values, form}) => {

                            const standardProperties = _standardProperties.map(property => ({...property, selected: values.selectedPropertyIds.includes(property.id)}));
                            const customProperties = _customProperties.map(property => ({...property, selected: values.selectedPropertyIds.includes(property.id)}));

                            const onSelectionToggled = (property) => {
                                const selectedPropertyIds = Array.from(values.selectedPropertyIds);
                                const index = selectedPropertyIds.indexOf(property.id);
                                if (index === -1) {
                                    selectedPropertyIds.push(property.id);
                                } else {
                                    selectedPropertyIds.splice(index, 1);
                                }
                                form.change('selectedPropertyIds', selectedPropertyIds);
                            };

                            return (
                                <>
                                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 2}}>
                                        <Divider
                                            orientation="horizontal"
                                            variant="middle"
                                            sx={{marginBlock: 1, '&:before, &:after': {borderTopStyle: 'dashed',}}}
                                        >
                                            <Typography variant={'body2'} fontWeight={600}>
                                                Template Details
                                            </Typography>
                                        </Divider>
                                        <MuiRFF.TextField
                                            fullWidth
                                            name={"title"}
                                            label={"Title"}
                                            required={required.title}
                                        />
                                        <Divider
                                            orientation="horizontal"
                                            variant="middle"
                                            sx={{marginBlock: 1, '&:before, &:after': {borderTopStyle: 'dashed',}}}
                                        >
                                            <Typography variant={'body2'} fontWeight={600}>
                                                Property selection {
                                                values.selectedPropertyIds.length > 0
                                                    ? <Typography component={'span'} variant={'caption'} color={'primary.main'} fontWeight={600}>
                                                        ({values.selectedPropertyIds.length} selected)
                                                    </Typography>
                                                    : ""
                                            }
                                            </Typography>
                                        </Divider>
                                        <SearchInput
                                            autoSearch
                                            initialValue={keywords}
                                            disabled={loadingProperties}
                                            search={(val) => setKeywords(val ?? "")}
                                            label={'Search'}
                                            variant={'outlined'}
                                            paperProps={{
                                                className: 'px-0'
                                            }}
                                        />
                                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 3, maxHeight: '40vh', overflowY: 'auto'}}>
                                            <div className={'d-flex flex-column'} style={{gap: 12}}>
                                                <Typography variant={'body1'} fontWeight={600}>
                                                    Standard Properties
                                                </Typography>
                                                {
                                                    loadingProperties &&
                                                    Array.from({length: 4}).map((_, i) => (
                                                        <Skeleton
                                                            key={`loading-${i}`}
                                                            variant="rectangular"
                                                            width={'100%'}
                                                            sx={(theme) => ({minHeight: 80, width: '100%', borderRadius: (theme).vars?.shape.borderRadius})}
                                                        />
                                                    ))
                                                }
                                                {
                                                    !loadingProperties && !standardProperties.length &&
                                                    <Typography variant={'body2'} textAlign={'center'} sx={{marginBlock: 10, color: 'text.secondary'}}>
                                                        {
                                                            !!keywords.trim().length &&
                                                            "No properties found for your active search. Please try again with a different keyword."
                                                        }
                                                        {
                                                            !keywords.trim().length &&
                                                            "No standard properties found. you may suggest one using the behind page."
                                                        }
                                                    </Typography>
                                                }
                                                {
                                                    !loadingProperties &&
                                                    standardProperties.map(property => (
                                                        <PropertyCard
                                                            key={property.id}
                                                            property={property}
                                                            typeNames={typeNames}
                                                            onSelectionToggled={onSelectionToggled}
                                                        />
                                                    ))
                                                }
                                            </div>
                                            {
                                                (loadingProperties || !!customProperties.length) &&
                                                <div className={'d-flex flex-column'} style={{gap: 12}}>
                                                    <Typography variant={'body1'} fontWeight={600}>
                                                        Custom Properties
                                                    </Typography>
                                                    {
                                                        loadingProperties &&
                                                        Array.from({length: 4}).map((_, i) => (
                                                            <Skeleton
                                                                key={`loading-${i}`}
                                                                variant="rectangular"
                                                                width={'100%'}
                                                                sx={(theme) => ({minHeight: 80, width: '100%', borderRadius: (theme).vars?.shape.borderRadius})}
                                                            />
                                                        ))
                                                    }
                                                    {
                                                        !loadingProperties && !customProperties.length &&
                                                        <Typography variant={'body2'} textAlign={'center'} sx={{marginBlock: 10, color: 'text.secondary'}}>
                                                            {
                                                                !!keywords.trim().length &&
                                                                "No properties found for your active search. Please try again with a different keyword."
                                                            }
                                                            {
                                                                !keywords.trim().length &&
                                                                "No custom properties found. you may create one using the behind page."
                                                            }
                                                        </Typography>
                                                    }
                                                    {
                                                        !loadingProperties &&
                                                        customProperties.map(property => (
                                                            <PropertyCard
                                                                key={property.id}
                                                                property={property}
                                                                typeNames={typeNames}
                                                                onSelectionToggled={onSelectionToggled}
                                                            />
                                                        ))
                                                    }
                                                </div>
                                            }
                                        </Box>
                                    </Box>
                                </>
                            );
                        }}
                    </Form>
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Button
                        variant={'text'}
                        type={'button'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Grid xs={'auto'} padding={0}>
                        <Button
                            variant={'contained'}
                            type={'submit'}
                            form={'upsert-template-form'}
                            color={'primary'}
                            size={'small'}
                            disabled={submitting}
                            sx={{padding: '4px 10px'}}
                        >
                            {
                                submitting
                                    ? (creating ? "Creating..." : "Updating...")
                                    : (creating ? "Create Template" : "Update Template")
                            }
                        </Button>
                    </Grid>
                </DialogActions>
            </Dialog>
        </>
    );
}

const PropertyCard = ({property, onSelectionToggled, typeNames}) => {
    const propertyTypeTitle = useMemo(() => typeNames[property.typeId], [property.typeId, typeNames]);

    return (
        <>
            <Card
                variant={'outlined'}
                sx={{padding: 1, cursor: 'pointer'}}
                className={'manage-product-technical-properties-dialog-card'}
                onClick={() => onSelectionToggled?.(property)}
            >
                <Grid container spacing={1} sx={{margin: 0}}>
                    <Grid xs={'auto'}>
                        <Checkbox
                            value={true}
                            checked={property.selected ?? false}
                            onChange={() => onSelectionToggled(property)}
                            labelClassName={'m-0'}
                            classes={{
                                root: 'p-0',
                            }}
                        />
                    </Grid>
                    <Grid xs>
                        <Grid container spacing={2} columns={12}>
                            <Grid xs>
                                <Typography variant={'body2'}>
                                    {property.title ?? ""}
                                    <Typography
                                        component={'span'}
                                        sx={{display: 'inline-block', marginInlineStart: 1}}
                                        variant={'body2'}
                                        color={'secondary.main'}
                                    >
                                        ({propertyTypeTitle})
                                    </Typography>
                                </Typography>
                            </Grid>
                            <Grid xs={'auto'} className={'d-flex align-items-center flex-wrap justify-content-end p-0'}>
                                <div className={classnames('checkmark-container')}>
                                    {
                                        property.hasPredefinedValues
                                            ? <SaveIcon className={classnames(`save`, "mr-1")}/>
                                            : <CancelIcon className={classnames(`cancel`, "mr-1")}/>
                                    }
                                    <Typography variant={'caption'}>
                                        Has Predefined values
                                    </Typography>
                                </div>
                                <div className={classnames('checkmark-container')}>
                                    {
                                        property.isMultiSelect
                                            ? <SaveIcon className={classnames(`save`, "mr-1")}/>
                                            : <CancelIcon className={classnames(`cancel`, "mr-1")}/>
                                    }
                                    <Typography variant={'caption'}>
                                        Multi-select
                                    </Typography>
                                </div>
                            </Grid>
                            <Grid xs={12}>
                                <Typography variant={'body2'}>
                                    Description: {property.description ?? "--"}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>
        </>
    );
}